import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

interface Product {
  sku: string;
  name?: string;
  warehouse: number;
  reserved: number;
  antaka: string;
  actual_stock: number;
}

export default function InventoryPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [products, setProducts] = useState<Product[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  // Filter state
  const [filterWarehouse, setFilterWarehouse] = useState(true);
  const [filterReserved, setFilterReserved] = useState(true);
  const [filterAntaka, setFilterAntaka] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const response = await fetch('/inventory-data.json');
        const data = await response.json();

        // Transform data
        const productsArray = data.products.map((item: any) => ({
          sku: item.sku,
          name: item.name,
          warehouse: item.warehouse,
          reserved: item.reserved,
          antaka: item.antaka,
          actual_stock: item.warehouse + item.reserved
        }));

        setProducts(productsArray);
        setLoading(false);
      } catch (error) {
        console.error('Failed to load data:', error);
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Apply filters
  const filteredProducts = products
    .filter(p => {
      // Search filter
      if (search && !p.sku.includes(search)) return false;

      // Inventory filter
      const hasWarehouse = p.warehouse > 0;
      const hasReserved = p.reserved > 0;
      const hasAntaka = p.antaka === 'متاح';

      const matchesFilter = (filterWarehouse && hasWarehouse) ||
                           (filterReserved && hasReserved) ||
                           (filterAntaka && hasAntaka);

      return matchesFilter;
    })
    .sort((a, b) => a.sku.localeCompare(b.sku, 'ar'));

  const warehouseTotal = filteredProducts.reduce((sum, p) => sum + p.warehouse, 0);
  const reservedTotal = filteredProducts.reduce((sum, p) => sum + p.reserved, 0);
  const actualStockTotal = filteredProducts.reduce((sum, p) => sum + p.actual_stock, 0);
  const antakaCount = filteredProducts.filter(p => p.antaka === 'متاح').length;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8 flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold mb-2">مخزن المنتجات 📦</h1>
          <p className="text-gray-600">إدارة المنتجات والمخزون - البيانات الصحيحة</p>
        </div>
        {user?.role === 'Owner' && (
          <button
            onClick={() => navigate('/inventory/edit')}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-700"
          >
            ✏️ تعديل البيانات
          </button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
          <p className="text-gray-600 text-sm">إجمالي المنتجات</p>
          <p className="text-3xl font-bold text-blue-600">{filteredProducts.length}</p>
        </div>
        <div className="bg-green-50 rounded-lg p-4 border border-green-200">
          <p className="text-gray-600 text-sm">في المخزن</p>
          <p className="text-3xl font-bold text-green-600">{warehouseTotal}</p>
        </div>
        <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
          <p className="text-gray-600 text-sm">إجمالي المخزون</p>
          <p className="text-3xl font-bold text-purple-600">{actualStockTotal}</p>
        </div>
        <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
          <p className="text-gray-600 text-sm">محجوز عند المورد</p>
          <p className="text-3xl font-bold text-orange-600">{reservedTotal}</p>
        </div>
      </div>

      {/* Inventory Tiers */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
          <p className="text-gray-600 text-sm">متاح عند المورد</p>
          <p className="text-2xl font-bold text-indigo-600">{antakaCount}</p>
          <p className="text-xs text-gray-500 mt-1">(منتج)</p>
        </div>
        <div className="bg-rose-50 rounded-lg p-4 border border-rose-200">
          <p className="text-gray-600 text-sm">الكمية الفعلية</p>
          <p className="text-2xl font-bold text-rose-600">{actualStockTotal}</p>
          <p className="text-xs text-gray-500 mt-1">(مخزن + محجوز)</p>
        </div>
        <div className="bg-cyan-50 rounded-lg p-4 border border-cyan-200">
          <p className="text-gray-600 text-sm">Shopify Stock</p>
          <p className="text-2xl font-bold text-cyan-600">{actualStockTotal}</p>
          <p className="text-xs text-gray-500 mt-1">(الكمية المتاحة)</p>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="mb-6 space-y-4">
        <input
          type="text"
          placeholder="بحث بـ SKU..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />

        {/* Filter Checkboxes */}
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
          <p className="font-semibold text-gray-700 mb-3">فلتر البيانات:</p>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={filterWarehouse}
                onChange={(e) => setFilterWarehouse(e.target.checked)}
                className="w-4 h-4 text-green-600 rounded"
              />
              <span className="ml-2 text-gray-700">
                ✅ في المخزن ({warehouseTotal})
              </span>
            </label>
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={filterReserved}
                onChange={(e) => setFilterReserved(e.target.checked)}
                className="w-4 h-4 text-indigo-600 rounded"
              />
              <span className="ml-2 text-gray-700">
                📌 محجوز عند المورد ({reservedTotal})
              </span>
            </label>
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={filterAntaka}
                onChange={(e) => setFilterAntaka(e.target.checked)}
                className="w-4 h-4 text-rose-600 rounded"
              />
              <span className="ml-2 text-gray-700">
                🏭 متاح عند المورد ({antakaCount})
              </span>
            </label>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center">
            <p className="text-gray-500">جاري تحميل البيانات...</p>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-500">لم يتم العثور على منتجات</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-100 border-b">
              <tr>
                <th className="px-6 py-3 text-right text-sm font-semibold text-gray-700">اسم المنتج</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-gray-700">SKU</th>
                <th className="px-6 py-3 text-center text-sm font-semibold text-gray-700">في المخزن</th>
                <th className="px-6 py-3 text-center text-sm font-semibold text-gray-700">محجوز</th>
                <th className="px-6 py-3 text-center text-sm font-semibold text-gray-700">الإجمالي</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-gray-700">عند المورد</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map((product, idx) => (
                <tr key={idx} className="border-b hover:bg-gray-50 transition">
                  <td className="px-6 py-4">
                    <span className="font-semibold text-gray-800">{product.name || `SKU: ${product.sku}`}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="font-mono font-semibold text-blue-600 text-sm">{product.sku}</span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    {product.warehouse > 0 ? (
                      <span className="font-bold text-green-600">{product.warehouse}</span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-center">
                    {product.reserved > 0 ? (
                      <span className="font-bold text-indigo-600">{product.reserved}</span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-center">
                    {product.actual_stock > 0 ? (
                      <span className="inline-block bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-bold">
                        {product.actual_stock}
                      </span>
                    ) : (
                      <span className="text-gray-400">0</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    {product.antaka === 'متاح' ? (
                      <span className="text-xs bg-green-50 p-2 rounded text-green-600">✅ متاح</span>
                    ) : (
                      <span className="text-xs bg-red-50 p-2 rounded text-red-600">❌ غير متاح</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer */}
      <div className="mt-6 text-center text-gray-500 text-sm">
        <p>عرض {filteredProducts.length} من {products.length} منتج</p>
      </div>
    </div>
  );
}
